/**
 * @fileoverview Example API Client (Contract-First Design)
 * @module api-client/clients/example
 *
 * 100% type-safe Example/Products API client generated from contract.
 * All methods, types, and validation come from EXAMPLE_API_CONTRACT.
 */

import {
	EXAMPLE_API_CONTRACT,
	type CreateProductPayload,
	type UpdateProductPayload,
	type ListProductsQuery,
} from '@monorepo/types';
import { ContractClient } from '../core/contract-client';
import type { HttpClient } from '../core/http-client';

/**
 * Example API Client
 *
 * Contract-first client for product management operations.
 * All methods are type-safe and validated against the API contract.
 *
 * @example
 * ```typescript
 * const exampleClient = new ExampleClient(httpClient);
 *
 * // Create product
 * const product = await exampleClient.create({
 *   name: 'Product 1',
 *   description: 'Description',
 *   price: 99.99,
 *   stock: 100,
 * });
 *
 * // List products with pagination
 * const result = await exampleClient.list({ page: 1, limit: 10 });
 *
 * // Get product by ID
 * const product = await exampleClient.getOne(1);
 *
 * // Update product
 * const updated = await exampleClient.update(1, { price: 89.99 });
 *
 * // Delete product
 * await exampleClient.delete(1);
 * ```
 */
export class ExampleClient extends ContractClient<typeof EXAMPLE_API_CONTRACT> {
	constructor(httpClient: HttpClient) {
		super(httpClient, EXAMPLE_API_CONTRACT, {
			validateResponses: true, // ✅ ENABLED: Validate responses against contract
			validateRequests: true, // ✅ ENABLED: Validate requests before sending
			throwOnValidationError: true, // Throw detailed errors on validation failure
		});
	}

	/**
	 * Create a new product
	 * POST /example/products
	 * @returns Promise<ProductApiResponse> - Wrapped response: { success, data: ProductEntity, timestamp }
	 */
	async create(payload: CreateProductPayload) {
		return this.execute('create', { body: payload });
	}

	/**
	 * List all products with pagination
	 * GET /example/products
	 * @returns Promise<PaginatedProductsApiResponse> - Wrapped response: { success, data: ProductEntity[], meta, timestamp }
	 */
	async list(query?: ListProductsQuery) {
		return this.execute('list', { query });
	}

	/**
	 * Get a single product by ID
	 * GET /example/products/:id
	 * @returns Promise<ProductApiResponse> - Wrapped response: { success, data: ProductEntity, timestamp }
	 */
	async getOne(id: number) {
		return this.execute('getOne', { params: { id } });
	}

	/**
	 * Update a product
	 * PATCH /example/products/:id
	 * @returns Promise<ProductApiResponse> - Wrapped response: { success, data: ProductEntity, timestamp }
	 */
	async update(id: number, payload: UpdateProductPayload) {
		return this.execute('update', { params: { id }, body: payload });
	}

	/**
	 * Delete a product
	 * DELETE /example/products/:id
	 * @returns Promise<DeleteProductApiResponse> - Wrapped response: { success, data: { message }, timestamp }
	 */
	async delete(id: number) {
		return this.execute('delete', { params: { id } });
	}
}
